import React, { useState } from "react";
import {
  FaVolumeUp,
  FaVolumeMute,
  FaTrash,
  FaMagic,
  FaStar,
  FaUndo,
  FaSmile
} from "react-icons/fa";

/**
 * Letter Names & Pronunciation Guide
 */
const LETTER_NAMES = {
  A: { name: "A", phonetic: "Ay", example: "Apple" },
  B: { name: "B", phonetic: "Bee", example: "Ball" },
  C: { name: "C", phonetic: "Cee", example: "Cat" },
  D: { name: "D", phonetic: "Dee", example: "Dog" },
  E: { name: "E", phonetic: "Eee", example: "Elephant" },
  F: { name: "F", phonetic: "Ef", example: "Fish" },
  G: { name: "G", phonetic: "Jee", example: "Giraffe" },
  H: { name: "H", phonetic: "Aitch", example: "Hat" },
  I: { name: "I", phonetic: "Eye", example: "Ice" },
  J: { name: "J", phonetic: "Jay", example: "Jam" },
  K: { name: "K", phonetic: "Kay", example: "Kite" },
  L: { name: "L", phonetic: "El", example: "Lion" },
  M: { name: "M", phonetic: "Em", example: "Monkey" },
  N: { name: "N", phonetic: "En", example: "Nest" },
  O: { name: "O", phonetic: "Oh", example: "Owl" },
  P: { name: "P", phonetic: "Pee", example: "Penguin" },
  Q: { name: "Q", phonetic: "Cue", example: "Queen" },
  R: { name: "R", phonetic: "Ar", example: "Rabbit" },
  S: { name: "S", phonetic: "Ess", example: "Sun" },
  T: { name: "T", phonetic: "Tee", example: "Tiger" },
  U: { name: "U", phonetic: "You", example: "Umbrella" },
  V: { name: "V", phonetic: "Vee", example: "Violin" },
  W: { name: "W", phonetic: "Double-You", example: "Whale" },
  X: { name: "X", phonetic: "Ex", example: "Xylophone" },
  Y: { name: "Y", phonetic: "Why", example: "Yak" },
  Z: { name: "Z", phonetic: "Zee", example: "Zebra" }
};

export default function AlphabetNameLearner() {
  const [spelledName, setSpelledName] = useState([]);
  const [activeLetter, setActiveLetter] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [aiSpeech, setAiSpeech] = useState(
    "Tap any letter to hear its name, or spell your name below!"
  );

  const alphabetList = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Text-to-speech engine
  const speakText = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      if (isMuted) return;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1.2;
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleLetterClick = (letter) => {
    setActiveLetter(letter);
    const letterInfo = LETTER_NAMES[letter];
    const speech = `Letter ${letter}! pronounced ${letterInfo.phonetic}, like in ${letterInfo.example}!`;
    setAiSpeech(speech);
    speakText(speech);

    // Add letter to spelled name array
    if (spelledName.length < 12) {
      setSpelledName((prev) => [...prev, letter]);
    }
  };

  const handleSpellFullWord = () => {
    if (spelledName.length === 0) {
      const emptyMsg = "Tap some letters above to build your name first!";
      setAiSpeech(emptyMsg);
      speakText(emptyMsg);
      return;
    }

    const nameString = spelledName.join("");
    const speech = `Awesome! You spelled ${nameString}! Let's say the letter names: ${spelledName.join(
      ", "
    )}!`;
    setAiSpeech(speech);
    speakText(speech);
  };

  const handleRemoveLast = () => {
    setSpelledName((prev) => prev.slice(0, -1));
  };

  const handleClearAll = () => {
    setSpelledName([]);
    setActiveLetter(null);
    const msg = "Cleared! Start building a new name!";
    setAiSpeech(msg);
    speakText(msg);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (!isMuted) {
      window.speechSynthesis.cancel();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitleGroup}>
            <div style={styles.headerIcon}>
              <FaSmile size={24} color="#2563EB" />
            </div>
            <div>
              <h1 style={styles.title}>Alphabet Name & Spelling Studio</h1>
              <p style={styles.subtitle}>
                Learn Letter Names & Spell Your Name!
              </p>
            </div>
          </div>

          <button
            onClick={toggleMute}
            style={styles.muteBtn}
            title="Toggle Audio"
          >
            {isMuted ? (
              <FaVolumeMute size={22} color="#EF4444" />
            ) : (
              <FaVolumeUp size={22} color="#FFFFFF" />
            )}
          </button>
        </div>

        {/* Speech Banner */}
        <div style={styles.speechBanner}>
          <FaMagic size={18} color="#D97706" />
          <p style={styles.speechText}>"{aiSpeech}"</p>
        </div>

        {/* Spelled Name Display Banner */}
        <div style={styles.nameDisplaySection}>
          <span style={styles.nameLabel}>YOUR SPELLED NAME:</span>
          <div style={styles.tilesContainer}>
            {spelledName.length === 0 ? (
              <span style={styles.placeholderText}>
                Tap letters below to spell your name...
              </span>
            ) : (
              spelledName.map((char, index) => (
                <div key={index} style={styles.nameTile}>
                  {char}
                </div>
              ))
            )}
          </div>

          <div style={styles.nameActionControls}>
            <button onClick={handleSpellFullWord} style={styles.readNameBtn}>
              <FaVolumeUp size={14} /> Read My Name
            </button>
            <button
              onClick={handleRemoveLast}
              style={styles.undoBtn}
              disabled={spelledName.length === 0}
            >
              <FaUndo size={14} /> Undo
            </button>
            <button
              onClick={handleClearAll}
              style={styles.clearBtn}
              disabled={spelledName.length === 0}
            >
              <FaTrash size={14} /> Clear
            </button>
          </div>
        </div>

        {/* Active Letter Detail Box */}
        {activeLetter && (
          <div style={styles.activeDetailCard}>
            <div style={styles.bigLetterBadge}>{activeLetter}</div>
            <div style={styles.letterInfoText}>
              <p style={styles.detailName}>
                Letter Name:{" "}
                <strong>"{LETTER_NAMES[activeLetter].phonetic}"</strong>
              </p>
              <p style={styles.detailExample}>
                <FaStar color="#EAB308" style={{ marginRight: 6 }} />
                Starts with:{" "}
                <strong>{LETTER_NAMES[activeLetter].example}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Alphabet Keyboard (A to Z) */}
        <div style={styles.alphabetGridSection}>
          <p style={styles.gridInstruction}>Tap any letter to hear its name:</p>
          <div style={styles.alphabetGrid}>
            {alphabetList.map((char) => (
              <button
                key={char}
                onClick={() => handleLetterClick(char)}
                style={{
                  ...styles.letterKey,
                  backgroundColor:
                    activeLetter === char ? "#2563EB" : "#FFFFFF",
                  color: activeLetter === char ? "#FFFFFF" : "#1E293B",
                  borderColor: activeLetter === char ? "#1D4ED8" : "#CBD5E1"
                }}
              >
                <span style={styles.keyLetter}>{char}</span>
                <span
                  style={{
                    ...styles.keySub,
                    color: activeLetter === char ? "#E0F2FE" : "#64748B"
                  }}
                >
                  {LETTER_NAMES[char].phonetic}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#F0F9FF",
    padding: "20px",
    boxSizing: "border-box",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    width: "100%",
    maxWidth: "860px",
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0 12px 30px -5px rgba(0, 0, 0, 0.12)",
    border: "4px solid #BAE6FD",
    overflow: "hidden"
  },
  header: {
    background: "linear-gradient(135deg, #0284C7 0%, #2563EB 100%)",
    padding: "18px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#FFFFFF"
  },
  headerTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    backgroundColor: "#FFFFFF",
    borderRadius: "50%",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  title: {
    margin: 0,
    fontSize: "1.35rem",
    fontWeight: "800"
  },
  subtitle: {
    margin: 0,
    fontSize: "0.85rem",
    opacity: 0.95
  },
  muteBtn: {
    background: "rgba(255, 255, 255, 0.2)",
    border: "none",
    borderRadius: "50%",
    width: "42px",
    height: "42px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  speechBanner: {
    backgroundColor: "#FEF3C7",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    borderBottom: "2px solid #FDE68A"
  },
  speechText: {
    margin: 0,
    color: "#92400E",
    fontWeight: "700",
    fontSize: "0.95rem"
  },
  nameDisplaySection: {
    padding: "20px",
    backgroundColor: "#F8FAFC",
    borderBottom: "2px solid #E2E8F0"
  },
  nameLabel: {
    fontSize: "0.75rem",
    fontWeight: "800",
    color: "#64748B",
    letterSpacing: "0.05em"
  },
  tilesContainer: {
    display: "flex",
    gap: "8px",
    minHeight: "56px",
    alignItems: "center",
    marginTop: "8px",
    flexWrap: "wrap"
  },
  placeholderText: {
    color: "#94A3B8",
    fontStyle: "italic",
    fontSize: "0.95rem"
  },
  nameTile: {
    width: "46px",
    height: "52px",
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: "800",
    boxShadow: "0 4px 6px -1px rgba(37, 99, 235, 0.3)"
  },
  nameActionControls: {
    display: "flex",
    gap: "10px",
    marginTop: "14px",
    flexWrap: "wrap"
  },
  readNameBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
    border: "none",
    padding: "8px 16px",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "0.85rem"
  },
  undoBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#E2E8F0",
    color: "#334155",
    border: "none",
    padding: "8px 14px",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "0.85rem"
  },
  clearBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    border: "none",
    padding: "8px 14px",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "0.85rem"
  },
  activeDetailCard: {
    margin: "16px 20px 0 20px",
    backgroundColor: "#E0F2FE",
    borderRadius: "16px",
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    border: "2px solid #7DD3FC"
  },
  bigLetterBadge: {
    width: "54px",
    height: "54px",
    backgroundColor: "#0284C7",
    color: "#FFFFFF",
    borderRadius: "14px",
    fontSize: "2rem",
    fontWeight: "900",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  letterInfoText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  detailName: {
    margin: 0,
    color: "#0369A1",
    fontSize: "0.95rem"
  },
  detailExample: {
    margin: 0,
    color: "#0F172A",
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center"
  },
  alphabetGridSection: {
    padding: "20px"
  },
  gridInstruction: {
    margin: "0 0 12px 0",
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#475569"
  },
  alphabetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(68px, 1fr))",
    gap: "10px"
  },
  letterKey: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 4px",
    borderRadius: "12px",
    border: "2px solid",
    cursor: "pointer",
    transition: "all 0.15s ease"
  },
  keyLetter: {
    fontSize: "1.25rem",
    fontWeight: "800"
  },
  keySub: {
    fontSize: "0.65rem",
    fontWeight: "bold",
    marginTop: "2px"
  }
};